/**
 * What an event looks like to somebody it is not open to (GH-871b, DEC-052).
 *
 * Owner, 2026-10-03: *"events are never accessible when logged out unless via event QR code scan or
 * at event via geolocation"*, and *"Donations can happen even when signed out."* So while
 * membership is on, an event page that answers `event_access_required` becomes this:
 *
 *   - **"Opens at the event"**, and how — scan the code on the screen, or share your location;
 *   - **Give**, first and green, exactly as on the event page: a gift is never gated. It mints the
 *     same signed checkout `/donation-link` mints for the page's own button (that route is never
 *     gated), so nothing about giving changes except that the event's details are not shown;
 *   - **Use my location**, behind the tap and never on load (SPEC.md §6.2). The position is
 *     rounded to two decimals inside `fetchEventAccessByLocation` before it leaves the page. A match
 *     for this event keeps its token and the page opens; anything else says which of the three it
 *     was — no open event here, location turned off, or no position — each with the way forward.
 *
 * Nothing here is scarcity and nothing names membership beyond the one line on listings: this sits
 * beside a Give button (DEC-051 keeps scarcity off every give surface).
 */
import { useState, type ReactNode } from "react";
import { ACCESS_COPY } from "@givehere/contracts";
import {
  ApiError,
  fetchDonationLink,
  fetchEventAccessByLocation,
  requestPositionForAccess,
} from "./api.js";
import { rememberEventAccess } from "./event-access.js";
import { Explain } from "./Explain.js";
import { LocationIcon } from "./icons.js";

type Locate =
  | { kind: "idle" }
  | { kind: "locating" }
  | { kind: "none" }
  | { kind: "off" }
  | { kind: "unavailable" }
  | { kind: "failed"; message: string };

/** The three outcomes that say what to do next, as heading and body. */
const OUTCOME: Record<"none" | "off" | "unavailable", { title: string; body: string }> = {
  none: { title: ACCESS_COPY.noOpenEventHere, body: ACCESS_COPY.noOpenEventHereBody },
  off: { title: ACCESS_COPY.locationOff, body: ACCESS_COPY.locationOffBody },
  unavailable: {
    title: ACCESS_COPY.locationUnavailable,
    body: ACCESS_COPY.locationUnavailableBody,
  },
};

/** True for the refusal that turns an event page into this state. */
export const isEventAccessRequired = (caught: unknown): boolean =>
  caught instanceof ApiError && caught.code === "event_access_required";

export function EventOpensAtEvent({
  eventId,
  onOpened,
  children,
}: {
  eventId: string;
  /** This event's token is now held: the host reads the page again. */
  onOpened: () => void;
  /** What the host keeps above the state (the big screen's auto-invite card). */
  children?: ReactNode;
}) {
  const [locate, setLocate] = useState<Locate>({ kind: "idle" });
  const [giving, setGiving] = useState(false);
  const [giveFailure, setGiveFailure] = useState<string | null>(null);

  const give = () => {
    setGiveFailure(null);
    setGiving(true);
    fetchDonationLink(eventId)
      .then((link) => window.location.assign(`/g/${encodeURIComponent(link.token)}`))
      .catch(() => {
        // The event page's own sentence: nothing was attempted and nothing was charged.
        setGiveFailure("That didn't open — try again.");
        setGiving(false);
      });
  };

  const locateMe = async () => {
    setLocate({ kind: "locating" });
    const fix = await requestPositionForAccess();
    if (fix.kind !== "position") {
      setLocate({ kind: fix.kind === "denied" ? "off" : "unavailable" });
      return;
    }
    try {
      const answer = await fetchEventAccessByLocation(fix);
      const events = answer?.events ?? [];
      // Every open event nearby is kept: the donor is standing at it.
      for (const found of events) {
        rememberEventAccess(found.eventId, found.accessToken, found.accessExpiresAt);
      }
      if (events.some((found) => found.eventId === eventId)) {
        setLocate({ kind: "idle" });
        onOpened();
        return;
      }
      setLocate({ kind: "none" });
    } catch (caught: unknown) {
      setLocate({
        kind: "failed",
        message: caught instanceof ApiError ? caught.message : "That didn’t work. Try again.",
      });
    }
  };

  const outcome =
    locate.kind === "none" || locate.kind === "off" || locate.kind === "unavailable"
      ? OUTCOME[locate.kind]
      : null;

  return (
    <main className="card card--landing eventpage">
      <a className="link eventpage__back" href="/">
        ‹ Directory
      </a>
      {children}
      <h1 className="landing__event eventpage__name">{ACCESS_COPY.eventOpensAtEvent}</h1>
      <p className="landing__about">{ACCESS_COPY.eventOpensAtEventBody}</p>

      <button type="button" className="pay landing__give" disabled={giving} onClick={give}>
        {giving ? "Opening…" : "Give"}
      </button>
      {giveFailure && (
        <p className="notice notice--error" role="alert">
          {giveFailure}
        </p>
      )}

      <button
        type="button"
        className="pay pay--outline event-access__locate"
        disabled={locate.kind === "locating"}
        onClick={() => void locateMe()}
      >
        <LocationIcon />
        <span>{locate.kind === "locating" ? "Finding you…" : ACCESS_COPY.useMyLocation}</span>
      </button>

      <div aria-live="polite">
        {outcome && (
          <div className="notice event-access__outcome" role="status">
            <p className="event-access__title">{outcome.title}</p>
            <p className="event-access__body">{outcome.body}</p>
          </div>
        )}
        {locate.kind === "failed" && (
          <p className="notice notice--error" role="alert">
            {locate.message}
          </p>
        )}
      </div>
    </main>
  );
}

/**
 * The one line a listing shows while events are for members (`eventsHidden: true`), in place of
 * an empty list or a "nothing on" that would be untrue. The sentence behind the ⓘ says how anyone
 * at an event opens it, and that everyone can give.
 */
export function EventsForMembers({ className = "notice" }: { className?: string }) {
  return (
    <Explain
      className={className}
      label={ACCESS_COPY.eventsForMembers}
      title={ACCESS_COPY.eventsForMembers}
    >
      <p>{ACCESS_COPY.eventsForMembersBody}</p>
    </Explain>
  );
}
